/**
 * Annual review statistics — a separate stats contract (CLAUDE.md §21.1 /
 * §37) for the unified statistics bar's "Annual reviews" set.
 *
 * READ-ONLY: every function here is pure over camelCase review_clients
 * rows (reviewClientsApi.js). Nothing here writes to review records, and
 * nothing here ever changes a review month — review_month is a permanent,
 * Adviser-owned anchor (§17 / §34.4).
 *
 * COUNTS MEETINGS, NOT CLIENTS: a group (members sharing group_id) counts
 * once, an ungrouped client counts once — the same unit the daily nudge
 * counts (§34.10).
 *
 * TEST RECORDS (is_test = true, §33) are excluded at source.
 *
 * SEPARATION, BOTH WAYS: this module imports only the review planner's
 * pure helpers (reviewPlanner.js) and the SAST date formatter in
 * dateHelpers.js, which counts nothing. It imports nothing from, and feeds
 * nothing into, dashboardStats.js, prepRequests.js, referralStats.js,
 * bonusCalculator.js, bonusClock.js or slaCalculator.js.
 */

import { isReviewOverdue, PIPELINE_REVIEW_STATUSES } from './reviewPlanner';
import { getTodaySASTDateString } from './dateHelpers';

/** Meeting key: the group id for a grouped client, else the client's own id. */
function meetingKey(client) {
  return client.groupId || client.id;
}

function realClients(clients) {
  return (Array.isArray(clients) ? clients : []).filter((c) => c && c.isTest !== true);
}

/**
 * Overdue review MEETINGS — a meeting is overdue when any of its members is
 * overdue by the planner's own checklist-aware isReviewOverdue (the same
 * rule as the planner's OVERDUE tag): review month passed (SAST), not
 * completed, and applicable checklist items not all ticked.
 *
 * @param {Array} clients — review client rows
 * @param {{year: number, month: number}} now — getCurrentSASTYearMonth()
 */
export function countOverdueReviewMeetings(clients, now) {
  const keys = new Set();
  for (const c of realClients(clients)) {
    if (isReviewOverdue(c, now)) keys.add(meetingKey(c));
  }
  return keys.size;
}

/** 'YYYY' of an ISO timestamp in Africa/Johannesburg, or null. */
function sastYearOf(iso) {
  if (typeof iso !== 'string') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return getTodaySASTDateString(d).slice(0, 4);
}

/**
 * The four Annual reviews tiles, all counted in MEETINGS:
 *
 *   dueThisMonth      — meetings anchored in the current SAST month and
 *                       year (review_year / review_month) that are not
 *                       completed. Completed ones show in Completed This
 *                       Year instead.
 *   inProgress        — meetings on the pipeline board: status
 *                       'submitted' or 'appointment_made'.
 *   completedThisYear — completed meetings whose completed_at falls in the
 *                       current SAST CALENDAR year — the same "This Year"
 *                       as the referrals' Converted This Year.
 *   overdue           — countOverdueReviewMeetings (the planner's
 *                       checklist-aware isReviewOverdue).
 *
 * @param {Array} clients — review client rows: the active set plus the
 *   completed set (both needed for Completed This Year)
 * @param {{year: number, month: number}} now — getCurrentSASTYearMonth()
 * @param {Date} [clock] — injectable clock for the SAST year (tests)
 */
export function computeReviewStats(clients, now, clock = new Date()) {
  const list = realClients(clients);
  const thisYear = getTodaySASTDateString(clock).slice(0, 4);
  const due = new Set();
  const active = new Set();
  const done = new Set();
  for (const c of list) {
    const key = meetingKey(c);
    if (c.status !== 'completed' && c.reviewYear === now.year && c.reviewMonth === now.month) {
      due.add(key);
    }
    if (PIPELINE_REVIEW_STATUSES.includes(c.status)) active.add(key);
    if (c.status === 'completed' && sastYearOf(c.completedAt) === thisYear) done.add(key);
  }
  return {
    dueThisMonth: due.size,
    inProgress: active.size,
    completedThisYear: done.size,
    overdue: countOverdueReviewMeetings(list, now),
  };
}
